import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Calculator, Plus, Pencil, RotateCcw, Trash2, X, Loader2, ShieldCheck, Info,
  BadgeCheck, CheckCircle2, AlertCircle, ChevronDown, Eraser,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';
import ApplicabilityContracts from '@/components/ApplicabilityContracts';
import LearningRecords from '@/components/LearningRecords';
import GoldenTests from '@/components/GoldenTests';
import ProcessHelp from '@/components/ProcessHelp';

const emptyComponent = () => ({ label: '', op: '+', sql: '', concepts: '' });

function conceptsToText(concepts) {
  // [[a,b],[c]] -> "a, b\nc"
  return (concepts || []).map((g) => (Array.isArray(g) ? g.join(', ') : String(g))).join('\n');
}
function textToConcepts(text) {
  // each line is a synonym group (comma-separated)
  return String(text || '')
    .split('\n')
    .map((line) => line.split(',').map((t) => t.trim()).filter(Boolean))
    .filter((g) => g.length);
}

export default function MetricRegistryPage() {
  const canWrite = useCan('measures:certify');
  const [tab, setTab] = useState('composites');
  const [metrics, setMetrics] = useState([]);
  const [scope, setScope] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new, object=edit
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [components, setComponents] = useState([emptyComponent()]);
  const [mode, setMode] = useState('wizard'); // 'wizard' (pick measures) | 'advanced' (type formulas)
  const [measureOptions, setMeasureOptions] = useState([]);
  const [preview, setPreview] = useState(null); // { loading, runs_in, missing_in, total_models }
  const [saving, setSaving] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const clearAiCache = async () => {
    setClearingCache(true);
    try {
      const r = await api.clearExploreCache();
      toast.success(r.cleared ? `Cleared ${r.cleared} cached AI answer${r.cleared === 1 ? '' : 's'}` : 'AI answer cache was already clear');
    } catch (e) { toast.error(e.message || 'Could not clear the AI answer cache'); }
    finally { setClearingCache(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, sc] = await Promise.all([
        api.getMetricRegistry(),
        api.getMetricScope().catch(() => ({ scope: {} })),
      ]);
      setMetrics(res.metrics || []);
      setScope(sc.scope || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load metric registry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getRegistryMeasures().then((r) => setMeasureOptions(r.measures || [])).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setName('');
    setAliases('');
    setComponents([emptyComponent()]);
    setMode('wizard');
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setName(m.name || '');
    setAliases((m.aliases || []).filter((a) => a.toLowerCase() !== (m.name || '').toLowerCase()).join(', '));
    setComponents(
      (m.components || []).length
        ? m.components.map((c) => ({
            label: c.label || '', op: c.op === '-' ? '-' : '+', sql: c.sql || '',
            concepts: conceptsToText(c.concepts),
          }))
        : [emptyComponent()],
    );
    // A composite whose components all match existing measures can be edited in
    // the wizard; otherwise default to advanced so the raw formulas stay visible.
    const allMeasures = (m.components || []).every((c) => (measureOptions || []).includes(c.label));
    setMode(allMeasures && (m.components || []).length ? 'wizard' : 'advanced');
    setDialogOpen(true);
  };

  const updateComponent = (i, patch) => {
    setComponents((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Metric name is required'); return; }
    let cleaned;
    if (mode === 'wizard') {
      // Build components from picked measures — no formula typing. The label IS
      // the existing measure; scoping keys off the measure name; the SQL hint
      // references that measure so AI Explore composes it from real measures.
      cleaned = components
        .filter((c) => c.label.trim())
        .map((c) => ({
          label: c.label.trim(), op: c.op,
          sql: `{${c.label.trim()}}`,
          concepts: [[c.label.trim()]],
        }));
    } else {
      cleaned = components
        .filter((c) => c.label.trim() && c.sql.trim())
        .map((c) => ({ label: c.label.trim(), op: c.op, sql: c.sql.trim(), concepts: textToConcepts(c.concepts) }));
    }
    if (!cleaned.length) {
      toast.error(mode === 'wizard' ? 'Pick at least one measure' : 'Add at least one component with a label and formula');
      return;
    }
    setSaving(true);
    try {
      await api.upsertMetric({
        key: editing?.key,
        name: name.trim(),
        aliases: aliases.split(',').map((a) => a.trim()).filter(Boolean),
        components: cleaned,
      });
      toast.success(`Saved "${name.trim()}"`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to save metric');
    } finally {
      setSaving(false);
    }
  };

  const reset = async (m) => {
    const isBuiltin = m.source === 'builtin';
    const verb = isBuiltin ? (m.overridden ? 'Reset to built-in default' : null) : 'Delete this custom metric';
    if (isBuiltin && !m.overridden) return;
    if (!window.confirm(`${verb}?`)) return;
    try {
      await api.resetMetric(m.key);
      toast.success(isBuiltin ? 'Reverted to built-in default' : 'Custom metric deleted');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to reset metric');
    }
  };

  const toggleCertify = async (m) => {
    try {
      await api.certifyMetric(m.key, !m.certified);
      toast.success(!m.certified ? `Certified "${m.name}"` : `Removed certification from "${m.name}"`);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to update certification');
    }
  };

  const combineExpr = (m) =>
    (m.components || [])
      .map((c, i) => (i === 0 ? `(${c.label})` : `${c.op === '-' ? '−' : '+'} (${c.label})`))
      .join(' ');

  // Live preview of the composite being built in the dialog.
  const previewComponents = components
    .map((c) => (mode === 'wizard'
      ? { label: (c.label || '').trim(), op: c.op, sql: `{${(c.label || '').trim()}}`, concepts: [[(c.label || '').trim()]] }
      : { label: (c.label || '').trim(), op: c.op, sql: (c.sql || '').trim(), concepts: textToConcepts(c.concepts) }))
    .filter((c) => c.label && (mode === 'wizard' || c.sql));
  const combinedFormula = previewComponents
    .map((c, i) => (i === 0 ? `(${c.label})` : `${c.op === '-' ? '−' : '+'} (${c.label})`))
    .join(' ');
  const previewKey = JSON.stringify(previewComponents.map((c) => [c.op, c.label, c.sql, c.concepts]));

  useEffect(() => {
    if (!dialogOpen || previewComponents.length === 0) { setPreview(null); return undefined; }
    let alive = true;
    setPreview((p) => ({ ...(p || {}), loading: true }));
    const t = setTimeout(() => {
      api.metricScopePreview(previewComponents)
        .then((r) => { if (alive) setPreview({ loading: false, ...r }); })
        .catch(() => { if (alive) setPreview(null); });
    }, 450);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, dialogOpen]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="metric-registry-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight flex items-center gap-3">
            <Calculator className="w-7 h-7 text-primary" /> Metric Registry
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Define composite metrics once. AI Explore always computes them by this exact definition — never a
            guessed or substituted metric — and refuses when the selected model lacks a required input.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ProcessHelp
            testId="metric-registry-help"
            title="Metric Registry"
            intro="Define composite metrics once so AI Explore always computes them by the exact same definition."
            steps={[
              'Click New composite to define a metric from a formula and its required inputs.',
              'AI Explore computes registry metrics by this definition — never a guessed substitute — and refuses when the model lacks a required input.',
              'Every steward edit is versioned so the change history is reviewable.',
            ]}
          />
          {canWrite && (
            <Button variant="outline" onClick={clearAiCache} disabled={clearingCache} data-testid="clear-ai-cache-btn" className="shrink-0" title="Clear all cached AI Explore answers for your tenant so metric changes show on the next ask instead of waiting out the 6-hour cache.">
              {clearingCache ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eraser className="w-4 h-4 mr-1.5" />} Clear AI cache
            </Button>
          )}
          {canWrite && tab === 'composites' && (
            <Button onClick={openNew} data-testid="add-metric-btn" className="shrink-0">
              <Plus className="w-4 h-4 mr-1.5" /> New composite
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-border/40" data-testid="registry-tabs">
        <button
          onClick={() => setTab('composites')}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === 'composites' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-composites"
        >Composites</button>
        <button
          onClick={() => setTab('applicability')}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === 'applicability' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-applicability"
        >Applicability contracts</button>
        <button
          onClick={() => setTab('learning')}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === 'learning' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-learning"
        >Learning</button>
        <button
          onClick={() => setTab('golden')}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === 'golden' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-golden"
        >Golden tests</button>
      </div>

      {tab === 'golden' ? (
        <GoldenTests />
      ) : tab === 'learning' ? (
        <LearningRecords />
      ) : tab === 'applicability' ? (
        <ApplicabilityContracts />
      ) : loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="metric-list">
          {metrics.map((m) => (
            <div
              key={m.key}
              className="bg-card/60 border border-border/40 rounded-2xl p-5 hover:border-primary/30 transition-all"
              data-testid={`metric-card-${m.key.replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-lg font-heading font-semibold text-foreground">{m.name}</div>
                <div className="flex items-center gap-1.5">
                  {m.certified && (
                    <Badge className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30 flex items-center gap-1" data-testid={`certified-badge-${m.key.replace(/[^a-z0-9]+/g, '-')}`}>
                      <BadgeCheck className="w-3 h-3" /> Certified
                    </Badge>
                  )}
                  {m.source === 'builtin' && !m.overridden && (
                    <Badge variant="secondary" className="text-[10px]">Built-in</Badge>
                  )}
                  {m.overridden && <Badge className="text-[10px] bg-amber-500/15 text-amber-500 border-amber-500/30">Customized</Badge>}
                  {m.source === 'custom' && <Badge className="text-[10px] bg-primary/15 text-primary border-primary/30">Custom</Badge>}
                </div>
              </div>
              <div className="text-sm font-mono text-primary/90 bg-muted/30 rounded-lg px-3 py-2 mb-3">
                {combineExpr(m)}
              </div>
              <div className="space-y-1.5 mb-3">
                {(m.components || []).map((c, i) => (
                  <div key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground/80">{c.op === '-' ? '−' : '+'} {c.label}</span>
                    {' = '}
                    <span className="font-mono">{c.sql}</span>
                  </div>
                ))}
              </div>
              {(m.aliases || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {m.aliases.slice(0, 6).map((a) => (
                    <span key={a} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground">{a}</span>
                  ))}
                </div>
              )}
              {scope[m.key] && (
                <div className="mb-3" data-testid={`scope-${m.key.replace(/[^a-z0-9]+/g, '-')}`}>
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [m.key]: !p[m.key] }))}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`scope-toggle-${m.key.replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {scope[m.key].runs_in.length > 0 ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                    )}
                    Runs in {scope[m.key].runs_in.length} of {scope[m.key].total_models} model{scope[m.key].total_models === 1 ? '' : 's'}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded[m.key] ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded[m.key] && (
                    <div className="mt-2 space-y-1 pl-1">
                      {scope[m.key].runs_in.map((r) => (
                        <div key={r.model_id} className="text-[11px] text-emerald-600/90 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3 h-3" /> {r.model_name}
                        </div>
                      ))}
                      {scope[m.key].missing_in.map((r) => (
                        <div key={r.model_id} className="text-[11px] text-amber-600/90 flex items-center gap-1.5">
                          <AlertCircle className="w-3 h-3" /> {r.model_name} — missing {r.missing.join(', ')}
                        </div>
                      ))}
                      {scope[m.key].total_models === 0 && (
                        <div className="text-[11px] text-muted-foreground">No models in this workspace yet.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {canWrite && (
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <Button
                    size="sm"
                    variant={m.certified ? 'secondary' : 'outline'}
                    onClick={() => toggleCertify(m)}
                    data-testid={`certify-metric-${m.key.replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <BadgeCheck className="w-3.5 h-3.5 mr-1" /> {m.certified ? 'Uncertify' : 'Certify'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`edit-metric-${m.key.replace(/[^a-z0-9]+/g, '-')}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  {m.source === 'custom' ? (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => reset(m)} data-testid={`delete-metric-${m.key.replace(/[^a-z0-9]+/g, '-')}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  ) : m.overridden ? (
                    <Button size="sm" variant="ghost" onClick={() => reset(m)} data-testid={`reset-metric-${m.key.replace(/[^a-z0-9]+/g, '-')}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="metric-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              {editing ? `Edit ${editing.name}` : 'New composite metric'}
            </DialogTitle>
            <DialogDescription>
              Define the composite formula and the model fields it requires. AI Explore computes it by this exact definition.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Metric name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Combined Ratio" data-testid="metric-name-input" disabled={editing?.source === 'builtin'} />
              {editing?.source === 'builtin' && (
                <p className="text-[11px] text-muted-foreground mt-1">Built-in name is fixed. Edit the formula and aliases below.</p>
              )}
            </div>
            <div>
              <Label>Aliases (comma-separated)</Label>
              <Input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="combined operating ratio, cor" data-testid="metric-aliases-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Phrases in a question that should trigger this metric.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Components</Label>
                <div className="flex items-center gap-1.5">
                  <div className="flex rounded-lg border border-border/50 overflow-hidden text-[11px]">
                    <button
                      onClick={() => setMode('wizard')}
                      className={`px-2.5 py-1 ${mode === 'wizard' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/40'}`}
                      data-testid="mode-wizard-btn"
                    >Pick measures</button>
                    <button
                      onClick={() => setMode('advanced')}
                      className={`px-2.5 py-1 ${mode === 'advanced' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/40'}`}
                      data-testid="mode-advanced-btn"
                    >Advanced</button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setComponents((p) => [...p, emptyComponent()])} data-testid="add-component-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/30 rounded-lg p-2.5 mb-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {mode === 'wizard'
                    ? 'Pick an existing measure for each component and choose +/−. No formulas to type — AI Explore composes the metric from those measures, and only offers it in models that have them.'
                    : 'Each component is a sub-formula combined with +/−. "Required concepts" (one group per line, synonyms comma-separated) are the field-name keywords the model must contain — if any group is missing, AI Explore says it cannot calculate the metric instead of substituting.'}
                </span>
              </div>
              <div className="space-y-3">
                {components.map((c, i) => (
                  <div key={i} className="border border-border/40 rounded-xl p-3 space-y-2 relative" data-testid={`component-row-${i}`}>
                    {components.length > 1 && (
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-destructive" onClick={() => setComponents((p) => p.filter((_, idx) => idx !== i))} data-testid={`remove-component-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className="flex gap-2">
                      <select value={c.op} onChange={(e) => updateComponent(i, { op: e.target.value })} className="h-9 rounded-md border border-border/50 bg-background px-2 text-sm" data-testid={`component-op-${i}`}>
                        <option value="+">+</option>
                        <option value="-">−</option>
                      </select>
                      {mode === 'wizard' ? (
                        <select
                          value={c.label}
                          onChange={(e) => updateComponent(i, { label: e.target.value })}
                          className="h-9 flex-1 rounded-md border border-border/50 bg-background px-2 text-sm"
                          data-testid={`component-measure-${i}`}
                        >
                          <option value="">Select a measure…</option>
                          {measureOptions.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
                        </select>
                      ) : (
                        <Input value={c.label} onChange={(e) => updateComponent(i, { label: e.target.value })} placeholder="Component label (e.g. Loss Ratio)" data-testid={`component-label-${i}`} />
                      )}
                    </div>
                    {mode === 'advanced' && (
                      <>
                        <Input value={c.sql} onChange={(e) => updateComponent(i, { sql: e.target.value })} placeholder="Formula, e.g. SUM(incurred losses) / NULLIF(SUM(earned premium), 0)" data-testid={`component-sql-${i}`} />
                        <textarea value={c.concepts} onChange={(e) => updateComponent(i, { concepts: e.target.value })} placeholder={'Required concepts (one group per line)\nloss, incurred, claim\npremium, earned'} rows={2} className="w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm font-mono" data-testid={`component-concepts-${i}`} />
                      </>
                    )}
                  </div>
                ))}
              </div>
              {mode === 'wizard' && measureOptions.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-2">No measures found in this workspace yet — switch to Advanced to type formulas.</p>
              )}
            </div>

            {combinedFormula && (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3" data-testid="wizard-preview">
                <div className="text-[11px] uppercase tracking-wide text-primary/80 font-semibold mb-1">Live preview</div>
                <div className="text-sm font-mono text-foreground mb-2" data-testid="wizard-preview-formula">
                  {name.trim() || 'This metric'} = {combinedFormula}
                </div>
                {preview?.loading ? (
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking models…</div>
                ) : preview ? (
                  <div className="text-[11px]" data-testid="wizard-preview-scope">
                    <div className="flex items-center gap-1.5 text-foreground/80">
                      {preview.runs_in?.length > 0 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <AlertCircle className="w-3.5 h-3.5 text-amber-500" />}
                      Runs in {preview.runs_in?.length || 0} of {preview.total_models || 0} model{(preview.total_models || 0) === 1 ? '' : 's'}
                    </div>
                    <div className="mt-1 space-y-0.5 pl-1">
                      {(preview.runs_in || []).map((r) => (
                        <div key={r.model_id} className="text-emerald-600/90 flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3" /> {r.model_name}</div>
                      ))}
                      {(preview.missing_in || []).map((r) => (
                        <div key={r.model_id} className="text-amber-600/90 flex items-center gap-1.5"><AlertCircle className="w-3 h-3" /> {r.model_name} — missing {r.missing.join(', ')}</div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-metric-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
